import { createSlice } from '@reduxjs/toolkit';

import type { TProduct } from 'api/models/TProduct';
import type { NormalizedError } from 'errors/types';

export type QuoteState = {
  isLoading: boolean;
  errors: NormalizedError[] | null;
  data: {
    code: string | null;
    products: TProduct[];
  };
  currentProduct: TProduct | null;
  isCompareView: boolean;
  tariffTypeFilter: string[] | null;
  isDualRate: boolean;
};

export const initialState: QuoteState = {
  isLoading: false,
  errors: null,
  data: {
    code: null,
    products: []
  },
  currentProduct: null,
  isCompareView: false,
  tariffTypeFilter: null,
  isDualRate: false
};

export const slice = createSlice({
  name: 'quote',
  initialState,
  reducers: {
    setInitialState: (state) => {
      Object.entries(initialState).forEach(
        ([key, value]) => (state[key] = value)
      );
    },
    setIsLoading: (state) => {
      state.isLoading = true;
      state.errors = initialState.errors;
    },
    setSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.data = payload;
      state.errors = initialState.errors;
    },
    setError: (state, { payload }) => {
      state.isLoading = false;
      state.data = initialState.data;
      state.errors = payload;
    },
    setCurrentProduct: (state, { payload }) => {
      state.currentProduct = payload;
    },
    setIsCompareView: (state, { payload }) => {
      state.isCompareView = payload;
    },
    setTariffTypeFilter: (state, { payload }) => {
      state.tariffTypeFilter = payload;
    },
    setIsDualRate: (state, { payload }) => {
      state.isDualRate = payload;
    },
    setPriorityProductByCode: (state, { payload: productCode }) => {
      const products = state.data.products;
      const priorityProductIndex = (products as TProduct[]).findIndex(
        (product) => product.code === productCode
      );

      const [priorityProduct] = products.splice(priorityProductIndex, 1);
      state.data.products.unshift(priorityProduct);
    }
  }
});

export const {
  setInitialState,
  setIsLoading,
  setSuccess,
  setCurrentProduct,
  setError,
  setIsCompareView,
  setTariffTypeFilter,
  setIsDualRate,
  setPriorityProductByCode
} = slice.actions;

export const selectSlice = (state) => state[slice.name];
export const selectData = (state) => selectSlice(state).data;
export const selectProducts = (state) => selectData(state).products;
export const selectTariffTypeFilter = (state) =>
  selectSlice(state).tariffTypeFilter;
export const selectFilteredProducts = (state) => {
  const allProducts = selectProducts(state);
  const tariffTypeFilter = selectTariffTypeFilter(state);

  if (!tariffTypeFilter) {
    return allProducts;
  }

  return allProducts.filter((product) => {
    const til = product.electricityTil || product.gasTil;
    return tariffTypeFilter?.includes(til?.tariffType);
  });
};
export const selectTariffTypes = (state) =>
  selectProducts(state).reduce((acc, product) => {
    const til = product.electricityTil || product.gasTil;
    return [...acc, til?.tariffType];
  }, []);
export const selectIsDualRate = (state) => selectSlice(state).isDualRate;

export const selectIsLoading = (state) => selectSlice(state).isLoading;
export const selectErrors = (state) => selectSlice(state).errors;

export const selectProductByCode = (productCode) => (state) => {
  const products = selectProducts(state);

  return products?.find((product) => product.code === productCode);
};
export const selectIsCompareView = (state) => selectSlice(state).isCompareView;

// currentProduct
export const selectCurrentProduct = (state): TProduct =>
  (selectSlice(state).currentProduct || {}) as TProduct;
export const selectProductName = (state) => selectCurrentProduct(state)?.name;
export const selectGasQuote = (state) => selectCurrentProduct(state)?.gasTil;
export const selectGasTariffCode = (state) => {
  const gasQuote = selectGasQuote(state);

  return gasQuote?.tariffCode;
};
export const selectElectricityQuote = (state) =>
  selectCurrentProduct(state)?.electricityTil;

export const selectElectricityTariffCode = (state) => {
  const electricityQuote = selectElectricityQuote(state);

  return electricityQuote?.tariffCode;
};

export const selectTariffEndsOn = (state) => {
  const electricityQuote = selectElectricityQuote(state);
  const gasQuote = selectGasQuote(state);

  return electricityQuote?.tariffEndsOn || gasQuote?.tariffEndsOn;
};

export const selectPriceGuranteedUntil = (state) => {
  const electricityQuote = selectElectricityQuote(state);
  const gasQuote = selectGasQuote(state);

  return (
    electricityQuote?.priceGuaranteedUntil || gasQuote?.priceGuaranteedUntil
  );
};

export const selectProductFeatures = (state) =>
  selectCurrentProduct(state)?.features;

export const selectSpecifics = (state) =>
  selectCurrentProduct(state)?.specifics;

export const selectTariffType = (state) => {
  const electricityQuote = selectElectricityQuote(state);
  const gasQuote = selectGasQuote(state);

  return electricityQuote?.tariffType || gasQuote?.tariffType;
};
export const selectTOUDefinition = (state) => {
  return selectCurrentProduct(state)?.touDefinition;
};
export const selectProductAnnualAmount = (state) =>
  selectCurrentProduct(state)?.annualAmount;
export const selectMonthlyAmount = (state) =>
  selectCurrentProduct(state)?.monthlyAmount;

export default slice.reducer;
